import { Container, Graphics, Text } from 'pixi.js';
import type { Application } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameLoop } from '../core/GameLoop';
import { Rng, fnv1a } from '../core/rng/Rng';
import { gameplayRng } from '../core/rng/RngStreams';
import { TAU, lerp, segPointDist2, dist2 } from '../core/math';
import { BulletPool, type Bounds } from '../sim/bullets/BulletPool';
import { bulletKindIndex, KIND_SPARK } from '../sim/bullets/bulletKinds';
import { buildPatternRegistry } from '../sim/bullets/patterns/library';
import { PhaseRunner, type PatternContext, type TrackStep } from '../sim/bullets/patterns/PatternEmitter';
import { toSteps } from '../sim/bullets/patterns/schema';
import { TelegraphSystem } from '../sim/combat/TelegraphSystem';
import { Player } from '../sim/entities/Player';
import { TextureFactory } from '../render/TextureFactory';
import { BulletRenderer } from '../render/BulletRenderer';
import { TelegraphRenderer } from '../render/TelegraphRenderer';
import { CameraContainer } from '../render/CameraContainer';
import { drawPlayer } from '../render/PlayerGfx';
import { Palette } from '../render/Palette';
import { Layer } from '../render/layers';
import { DebugFlags } from '../debug/DebugFlags';

const ARENA: Bounds = { minX: -1200, minY: -700, maxX: 1200, maxY: 700 };
const BULLET_BOUNDS: Bounds = { minX: -1260, minY: -760, maxX: 1260, maxY: 760 };

const TURRET_RADIUS = 22;
const TURRET_HP = 60;
const TURRET_RESPAWN_STEPS = 240;
const SHOT_DAMAGE = 3;
const SHOT_COOLDOWN_STEPS = 9;

interface Turret {
  x: number;
  y: number;
  hp: number;
  respawn: number;
  flash: number;
  runner: PhaseRunner;
  gfx: Graphics;
}

/**
 * P1 proof arena (`?stress`): turrets cycle authored patterns; hotkeys pile on
 * load. Kept permanently as the perf regression harness and pattern-authoring
 * playground (`?pattern=<id>` makes every turret fire just that pattern).
 */
export class StressScene implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private camera = new CameraContainer();
  private player = new Player();
  private playerGfx: Graphics;
  private playerFlash = 0;

  private enemyBullets = new BulletPool(4096);
  private playerShots = new BulletPool(512);
  private particles = new BulletPool(1024);
  private telegraphs = new TelegraphSystem();

  private enemyRenderer: BulletRenderer;
  private shotRenderer: BulletRenderer;
  private particleRenderer: BulletRenderer;
  private telegraphRenderer: TelegraphRenderer;

  private turrets: Turret[] = [];
  private shotCooldown = 0;
  private ctx: PatternContext;

  constructor(
    private readonly app: Application,
    private readonly loop: GameLoop,
    textures: TextureFactory,
  ) {
    this.root.addChild(this.camera);
    this.camera.sortableChildren = true;

    // Arena backdrop: faint grid + border.
    const bg = new Graphics();
    bg.rect(ARENA.minX - 40, ARENA.minY - 40, (ARENA.maxX - ARENA.minX) + 80, (ARENA.maxY - ARENA.minY) + 80)
      .fill(Palette.bg);
    for (let gx = ARENA.minX; gx <= ARENA.maxX; gx += 120) {
      bg.moveTo(gx, ARENA.minY).lineTo(gx, ARENA.maxY);
    }
    for (let gy = ARENA.minY; gy <= ARENA.maxY; gy += 120) {
      bg.moveTo(ARENA.minX, gy).lineTo(ARENA.maxX, gy);
    }
    bg.stroke({ width: 1, color: 0x1a2032 });
    bg.rect(ARENA.minX, ARENA.minY, ARENA.maxX - ARENA.minX, ARENA.maxY - ARENA.minY)
      .stroke({ width: 2, color: 0x2a3450 });
    bg.zIndex = Layer.Floor;
    this.camera.addChild(bg);

    this.telegraphRenderer = new TelegraphRenderer(this.telegraphs);
    this.telegraphRenderer.gfx.zIndex = Layer.Telegraphs;
    this.camera.addChild(this.telegraphRenderer.gfx);

    this.shotRenderer = new BulletRenderer(this.playerShots, textures.frames);
    this.shotRenderer.container.zIndex = Layer.PlayerFx;
    this.camera.addChild(this.shotRenderer.container);

    this.playerGfx = drawPlayer();
    this.playerGfx.zIndex = Layer.Entities;
    this.camera.addChild(this.playerGfx);

    this.enemyRenderer = new BulletRenderer(this.enemyBullets, textures.frames);
    this.enemyRenderer.container.zIndex = Layer.EnemyBullets;
    this.camera.addChild(this.enemyRenderer.container);

    this.particleRenderer = new BulletRenderer(this.particles, textures.frames, true);
    this.particleRenderer.container.zIndex = Layer.Fx;
    this.camera.addChild(this.particleRenderer.container);

    // Turrets in a ring, each cycling patterns offset by index (deterministic).
    const registry = buildPatternRegistry();
    const cycle: string[] = DebugFlags.pattern && registry.has(DebugFlags.pattern)
      ? [DebugFlags.pattern]
      : ['twinSpiral', 'mercyWall', 'slamAndSeek', 'stressRings'];
    const count = 6;
    for (let i = 0; i < count; i++) {
      const a = (i * TAU) / count;
      const steps: TrackStep[] = [{
        loop: [
          { pattern: cycle[i % cycle.length]! },
          { wait: 1.2 },
        ],
      }];
      const turret: Turret = {
        x: Math.cos(a) * 480,
        y: Math.sin(a) * 480,
        hp: TURRET_HP,
        respawn: 0,
        flash: 0,
        runner: new PhaseRunner(registry, [steps], new Rng(fnv1a(`stress:${i}`)), toSteps),
        gfx: drawTurret(),
      };
      turret.gfx.position.set(turret.x, turret.y);
      turret.gfx.zIndex = Layer.Entities;
      this.camera.addChild(turret.gfx);
      this.turrets.push(turret);
    }

    this.player.place(0, 0);
    this.ctx = {
      playerX: 0,
      playerY: 0,
      bullets: this.enemyBullets,
      telegraphs: this.telegraphs,
    };

    const hint = new Text({
      text: 'STRESS  [B] +250 bullets   [C] clear   move: WASD/stick',
      style: { fontFamily: 'monospace', fontSize: 12, fill: Palette.hudDim },
    });
    hint.position.set(8, app.screen.height - 24);
    this.root.addChild(hint);
  }

  enter(): void {
    window.addEventListener('keydown', this.onKey);
  }

  exit(): void {
    window.removeEventListener('keydown', this.onKey);
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.code === 'KeyB') {
      for (let i = 0; i < 250; i++) {
        const idx = this.enemyBullets.spawn(
          gameplayRng.range(-300, 300), gameplayRng.range(-200, 200),
          gameplayRng.range(0, TAU), gameplayRng.range(40, 160),
          bulletKindIndex('orb', gameplayRng.bool() ? 'pink' : 'orange'),
          5, 600,
        );
        if (idx < 0) break;
      }
    } else if (e.code === 'KeyC') {
      this.enemyBullets.clear();
      this.playerShots.clear();
      this.particles.clear();
      this.telegraphs.clear();
    }
  };

  extraStats(): string {
    return (
      `bullets ${this.enemyBullets.count}/${this.enemyBullets.capacity}  ` +
      `shots ${this.playerShots.count}  particles ${this.particles.count}  ` +
      `tele ${this.telegraphs.list.length}  hp ${this.player.hp}/${this.player.maxHp}`
    );
  }

  update(dt: number, input: InputSnapshot): void {
    const player = this.player;
    this.camera.tick(dt);
    if (this.playerFlash > 0) this.playerFlash--;
    player.update(dt, input, ARENA);
    this.ctx.playerX = player.x;
    this.ctx.playerY = player.y;

    // Turrets: respawn + pattern emission.
    for (const t of this.turrets) {
      if (t.flash > 0) t.flash--;
      if (t.respawn > 0) {
        if (--t.respawn === 0) {
          t.hp = TURRET_HP;
          t.gfx.visible = true;
        }
        continue;
      }
      t.runner.tick(this.ctx, t.x, t.y);
    }

    this.enemyBullets.update(dt, player.x, player.y, BULLET_BOUNDS);
    this.playerShots.update(dt, player.x, player.y, BULLET_BOUNDS);
    this.particles.update(dt, player.x, player.y, BULLET_BOUNDS);

    this.telegraphs.update((zone) => {
      if (dist2(zone.x, zone.y, player.x, player.y) <= zone.radius * zone.radius) {
        this.damagePlayer(zone.damage);
      }
    });

    this.collideBulletsVsPlayer();
    this.collideShotsVsTurrets();
    this.firePlayerWeapon();
  }

  /** Swept many-vs-one: segment(prev→cur) vs the player's 4px core. */
  private collideBulletsVsPlayer(): void {
    const player = this.player;
    if (player.iframes > 0) return; // bullets pass through; never despawn them
    const pool = this.enemyBullets;
    const { x, y, px, py, radius } = pool;
    const hx = player.x;
    const hy = player.y;
    const hr = player.hitRadius;
    for (let i = 0; i < pool.count; i++) {
      const rr = radius[i]! + hr;
      // AABB early-out rejects >95% with four comparisons.
      const bx = x[i]!;
      const by = y[i]!;
      const bpx = px[i]!;
      const bpy = py[i]!;
      const minX = (bx < bpx ? bx : bpx) - rr;
      const maxX = (bx > bpx ? bx : bpx) + rr;
      const minY = (by < bpy ? by : bpy) - rr;
      const maxY = (by > bpy ? by : bpy) + rr;
      if (hx < minX || hx > maxX || hy < minY || hy > maxY) continue;
      if (segPointDist2(bpx, bpy, bx, by, hx, hy) <= rr * rr) {
        pool.despawn(i);
        i--;
        this.damagePlayer(1);
        if (player.iframes > 0) return; // hit landed; rest pass through
      }
    }
  }

  private collideShotsVsTurrets(): void {
    const pool = this.playerShots;
    for (let i = 0; i < pool.count; i++) {
      const sx = pool.x[i]!;
      const sy = pool.y[i]!;
      for (const t of this.turrets) {
        if (t.respawn > 0) continue;
        const rr = TURRET_RADIUS + pool.radius[i]!;
        if (dist2(sx, sy, t.x, t.y) <= rr * rr) {
          pool.despawn(i);
          i--;
          t.hp -= SHOT_DAMAGE;
          t.flash = 3;
          this.spawnSparks(t.x, t.y, 3, 0.9);
          if (t.hp <= 0) {
            t.respawn = TURRET_RESPAWN_STEPS;
            t.gfx.visible = false;
            this.spawnSparks(t.x, t.y, 24, 1.4);
            this.camera.addTrauma(0.2);
            this.loop.hitstop(0.04);
          }
          break;
        }
      }
    }
  }

  private firePlayerWeapon(): void {
    if (this.shotCooldown > 0) {
      this.shotCooldown--;
      return;
    }
    const player = this.player;
    let best: Turret | null = null;
    let bestD = Infinity;
    for (const t of this.turrets) {
      if (t.respawn > 0) continue;
      const d = dist2(player.x, player.y, t.x, t.y);
      if (d < bestD) {
        bestD = d;
        best = t;
      }
    }
    if (!best) return;
    this.shotCooldown = SHOT_COOLDOWN_STEPS;
    const aim = Math.atan2(best.y - player.y, best.x - player.x);
    const kind = bulletKindIndex('diamond', 'cyan');
    for (const off of [-0.06, 0.06]) {
      this.playerShots.spawn(player.x, player.y, aim + off, 520, kind, 4, 90, 0.7);
    }
  }

  private damagePlayer(damage: number): void {
    if (DebugFlags.god) return;
    if (!this.player.hit(damage)) return;
    this.playerFlash = 4;
    this.camera.addTrauma(0.45);
    this.loop.hitstop(0.09);
    if (!this.player.alive) {
      // Stress arena never ends — refill and keep dodging.
      this.player.hp = this.player.maxHp;
      this.player.alive = true;
    }
  }

  private spawnSparks(x: number, y: number, count: number, scale: number): void {
    for (let i = 0; i < count; i++) {
      this.particles.spawn(
        x, y, gameplayRng.range(0, TAU), gameplayRng.range(60, 260),
        KIND_SPARK, 0, gameplayRng.intRange(18, 40), scale * gameplayRng.range(0.4, 0.9),
      );
    }
  }

  render(alpha: number): void {
    const player = this.player;
    const ix = lerp(player.prevX, player.x, alpha);
    const iy = lerp(player.prevY, player.y, alpha);
    this.camera.follow(ix, iy, this.app.screen.width, this.app.screen.height);

    this.playerGfx.position.set(ix, iy);
    this.playerGfx.tint = this.playerFlash > 0 ? Palette.danger : 0xffffff;
    this.playerGfx.alpha = player.iframes > 0 ? (Math.floor(player.iframes / 4) % 2 ? 0.35 : 0.9) : 1;

    for (const t of this.turrets) {
      if (t.respawn > 0) continue;
      t.gfx.tint = t.flash > 0 ? 0xffffff : 0xffffff;
      t.gfx.alpha = t.flash > 0 ? 1 : 0.9;
      t.gfx.scale.set(t.flash > 0 ? 1.12 : 1);
    }

    this.enemyRenderer.sync(alpha);
    this.shotRenderer.sync(alpha);
    this.particleRenderer.sync(alpha);
    this.telegraphRenderer.sync();
  }
}

function drawTurret(): Graphics {
  const g = new Graphics();
  for (const [r, a] of [[34, 0.05], [29, 0.08]] as const) {
    g.circle(0, 0, r).fill({ color: Palette.enemyHot, alpha: a });
  }
  g.circle(0, 0, TURRET_RADIUS).fill(Palette.enemyBody);
  g.circle(0, 0, TURRET_RADIUS).stroke({ width: 2.5, color: Palette.enemyHot });
  g.poly([0, -10, 10, 0, 0, 10, -10, 0]).fill({ color: Palette.enemyHot, alpha: 0.9 });
  g.circle(0, 0, 3.5).fill(0xffffff);
  return g;
}
